import { useEffect, useState } from "react";
import { GripVertical, Pencil, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./adminApi";
import { imgUrl } from "../lib/spa";
import { Switch } from "../components/ui/switch";
import { MassageForm } from "./MassageForm";

export default function AdminMassages() {
  const [items, setItems] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const load = () => {
    api.get("/admin/massages").then((r) => setItems(r.data));
    api.get("/admin/therapists").then((r) => setTherapists(r.data));
  };
  useEffect(load, []);

  const quickUpdate = async (m, patch) => {
    setItems((list) => list.map((x) => (x.slug === m.slug ? { ...x, ...patch } : x)));
    try {
      await api.put(`/admin/massages/${m.slug}`, patch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao atualizar");
      load();
    }
  };

  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setItems(next);
    setDragIndex(null);
    setOverIndex(null);
    try {
      await api.put("/admin/massages-reorder", { slugs: next.map((m) => m.slug) });
      toast.success("Ordem guardada");
    } catch (err) {
      toast.error("Erro ao guardar a ordem");
      load();
    }
  };

  return (
    <div data-testid="admin-massages-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-light text-white">Massagens</h1>
        <p className="text-xs text-white/40">Arraste as linhas para reordenar</p>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
        {items.map((m, i) => (
          <div
            key={m.slug}
            data-testid={`admin-massage-row-${m.slug}`}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIndex(i);
            }}
            onDragLeave={() => setOverIndex(null)}
            onDrop={() => handleDrop(i)}
            onDragEnd={() => {
              setDragIndex(null);
              setOverIndex(null);
            }}
            className={`flex flex-wrap items-center gap-4 border-b border-white/5 bg-[#121212] px-4 py-3 last:border-0 transition-colors ${
              m.active === false ? "opacity-50" : ""
            } ${overIndex === i && dragIndex !== null && dragIndex !== i ? "border-t-2 border-t-gold bg-gold/5" : ""} ${
              dragIndex === i ? "opacity-40" : ""
            }`}
          >
            <span data-testid={`admin-massage-drag-${m.slug}`} className="cursor-grab text-white/30 active:cursor-grabbing">
              <GripVertical className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <img src={imgUrl(m.cover)} alt={m.name?.pt} className="h-12 w-16 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="flex items-center gap-2 font-medium text-white">
                {m.name?.pt}
                {m.featured && <Star className="h-3.5 w-3.5 fill-gold text-gold" />}
                {m.badge?.pt && (
                  <span className="rounded-full bg-gold px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-black">
                    {m.badge.pt}
                  </span>
                )}
              </p>
              <p className="truncate text-xs text-white/40">
                {m.duration} · {m.therapists?.length || 0} terapeutas · ordem {m.order}
              </p>
            </div>
            <label className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50">
              <Switch
                data-testid={`admin-massage-active-toggle-${m.slug}`}
                checked={m.active !== false}
                onCheckedChange={(v) => quickUpdate(m, { active: v })}
              />
              Ativa
            </label>
            <button
              data-testid={`admin-edit-massage-${m.slug}`}
              onClick={() => setEditing(m)}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 transition-colors hover:border-gold hover:text-gold"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              Editar
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="p-8 text-center text-sm text-white/40">Sem massagens.</p>}
      </div>

      {editing && (
        <MassageForm
          massage={editing}
          therapists={therapists}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
